import { useState } from 'react'
import { Plus, ImagePlus, ChevronDown } from 'lucide-react'
import type { Doc, Id } from '../../lib/api'
import type { StartupRow, DraftPatch } from '../../lib/draftToPublicPage'
import { useUpload } from '../../lib/useUpload'
import { SOCIAL_FIELDS } from '../../lib/startupMeta'
import { StartupCard } from './StartupCard'

type Draft = Doc<'pages'>['draft']

const inputCls =
  'w-full rounded-lg border border-zinc-700 bg-zinc-950 px-3 py-2.5 text-sm text-white outline-none focus:border-fuchsia-500'

export function PageTab({
  draft,
  avatarUrl,
  startups,
  onDraftChange,
  onUploadAvatar,
  onAddStartup,
  onSaveStartup,
  onRemoveStartup,
  onReorder,
}: {
  draft: Draft
  avatarUrl: string | null | undefined
  startups: StartupRow[]
  onDraftChange: (patch: DraftPatch) => void
  onUploadAvatar: (file: File) => void
  onAddStartup: () => void
  onSaveStartup: (id: Id<'pageStartups'>, patch: Record<string, unknown>) => void
  onRemoveStartup: (id: Id<'pageStartups'>) => void
  onReorder: (orderedIds: Id<'pageStartups'>[]) => void
}) {
  const { uploading } = useUpload()
  const [showSocial, setShowSocial] = useState(false)

  function move(index: number, dir: -1 | 1) {
    const next = [...startups]
    const j = index + dir
    if (j < 0 || j >= next.length) return
    ;[next[index], next[j]] = [next[j], next[index]]
    onReorder(next.map((s) => s._id))
  }

  return (
    <div className="space-y-6">
      {/* Profile */}
      <section className="rounded-xl border border-zinc-800 bg-zinc-900 p-4">
        <div className="flex items-start gap-4">
          <label className="flex h-16 w-16 flex-shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-full border border-zinc-700 bg-zinc-950 text-zinc-500 hover:border-zinc-600">
            {avatarUrl ? (
              <img src={avatarUrl} alt="" className="h-full w-full object-cover" />
            ) : uploading ? (
              <span className="text-xs">…</span>
            ) : (
              <ImagePlus className="h-5 w-5" />
            )}
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => e.target.files?.[0] && onUploadAvatar(e.target.files[0])}
            />
          </label>
          <div className="flex-1 space-y-2">
            <input
              defaultValue={draft.displayName}
              onChange={(e) => onDraftChange({ displayName: e.target.value })}
              placeholder="Your name"
              className={inputCls}
            />
            <input
              defaultValue={draft.location ?? ''}
              onChange={(e) => onDraftChange({ location: e.target.value })}
              placeholder="Where are you based?"
              className={inputCls}
            />
          </div>
        </div>

        <textarea
          defaultValue={draft.bio ?? ''}
          onChange={(e) => onDraftChange({ bio: e.target.value })}
          placeholder="Your bio. Markdown supported — **bold**, *italic*, [links](https://…)"
          rows={3}
          className={inputCls + ' mt-3 resize-y'}
        />

        {/* Social links */}
        <button
          onClick={() => setShowSocial((v) => !v)}
          className="mt-3 flex items-center gap-1 text-xs font-medium text-zinc-400 hover:text-white"
        >
          <ChevronDown className={`h-3.5 w-3.5 transition ${showSocial ? 'rotate-180' : ''}`} />
          Social links
        </button>
        {showSocial && (
          <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
            {SOCIAL_FIELDS.map((f) => (
              <input
                key={f.key}
                defaultValue={(draft.social as Record<string, string>)?.[f.key] ?? ''}
                onChange={(e) =>
                  onDraftChange({ social: { ...draft.social, [f.key]: e.target.value } })
                }
                placeholder={`${f.label} — ${f.placeholder}`}
                className={inputCls}
              />
            ))}
          </div>
        )}

        {/* Email collection */}
        <label className="mt-4 flex items-center justify-between rounded-lg bg-zinc-950 px-3 py-2.5">
          <span className="text-sm text-zinc-300">Collect email subscribers</span>
          <input
            type="checkbox"
            checked={draft.emailCollection?.enabled ?? false}
            onChange={(e) => onDraftChange({ emailCollection: { enabled: e.target.checked } })}
            className="h-4 w-4 accent-fuchsia-500"
          />
        </label>
        {draft.emailCollection?.enabled && (
          <div className="mt-3 space-y-2">
            <input
              defaultValue={draft.emailCollection?.headline ?? ''}
              onChange={(e) => onDraftChange({ emailCollection: { headline: e.target.value } })}
              placeholder="Form headline — e.g. “Subscribe for updates”"
              className={inputCls}
            />
            <select
              value={draft.emailCollection?.provider ?? 'native'}
              onChange={(e) =>
                onDraftChange({
                  emailCollection: {
                    provider: e.target.value as 'native' | 'substack' | 'beehiiv',
                  },
                })
              }
              className={inputCls}
            >
              <option value="native">Collect on MakerStack (export anytime)</option>
              <option value="substack">Substack</option>
              <option value="beehiiv">Beehiiv</option>
            </select>
            {draft.emailCollection?.provider === 'substack' && (
              <input
                defaultValue={draft.emailCollection?.substackHandle ?? ''}
                onChange={(e) =>
                  onDraftChange({ emailCollection: { substackHandle: e.target.value } })
                }
                placeholder="Substack handle (e.g. yourname)"
                className={inputCls}
              />
            )}
            {draft.emailCollection?.provider === 'beehiiv' && (
              <>
                <input
                  defaultValue={draft.emailCollection?.beehiivPublicationId ?? ''}
                  onChange={(e) =>
                    onDraftChange({ emailCollection: { beehiivPublicationId: e.target.value } })
                  }
                  placeholder="Beehiiv publication ID (pub_…)"
                  className={inputCls}
                />
                <input
                  defaultValue={draft.emailCollection?.beehiivApiKey ?? ''}
                  onChange={(e) =>
                    onDraftChange({ emailCollection: { beehiivApiKey: e.target.value } })
                  }
                  placeholder="Beehiiv API key"
                  className={inputCls}
                />
              </>
            )}
            <p className="text-xs text-zinc-500">
              Subscribers are collected on MakerStack and exportable from Settings. Substack/Beehiiv
              sync is coming soon.
            </p>
          </div>
        )}
      </section>

      {/* Startups */}
      <section>
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
            Your startups
          </h3>
        </div>
        <div className="space-y-2">
          {startups.map((s, i) => (
            <StartupCard
              key={s._id}
              startup={s}
              isFirst={i === 0}
              isLast={i === startups.length - 1}
              onSave={(patch) => onSaveStartup(s._id, patch)}
              onRemove={() => onRemoveStartup(s._id)}
              onMove={(dir) => move(i, dir)}
            />
          ))}
        </div>
        <button
          onClick={onAddStartup}
          className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-fuchsia-500 py-3 text-sm font-bold text-white hover:bg-fuchsia-400"
        >
          <Plus className="h-4 w-4" /> Add startup
        </button>
      </section>
    </div>
  )
}
